import { CheckmarkFilled } from "@carbon/icons-react";
import { TableToolbar, TableToolbarContent } from "@carbon/react";
import { NullableTableColumns } from "@flaminem/ui-components";
import { FC, useMemo, useState } from "react";

import { ModalTransition } from "src/components/Modal";
import { FixedTable } from "src/components/Tables/FixedTable";
import { TableToolbarSearch } from "src/components/Tables/TableToolbarSearch";
import { defaultPageSize } from "src/config/table-config";
import { useT } from "src/internationalization";
import { BlacklistableCaseIdentifier } from "src/types/case";

import { RelatedCasesModal } from "./RelatedCasesModal";

export type BlacklistableIdentifiersTableProps = {
  blacklistableIdentifiers: BlacklistableCaseIdentifier[];
  caseId: string;
  handleSelect: (selectedIdentifiers: BlacklistableCaseIdentifier[]) => void;
};

export const BlacklistableIdentifiersTable: FC<BlacklistableIdentifiersTableProps> = ({
  blacklistableIdentifiers,
  caseId,
  handleSelect,
}) => {
  const [selectedIdentifierId, setSelectedIdentifierId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const t = useT();

  const sortedIdentifiers = useMemo(
    () =>
      blacklistableIdentifiers.toSorted(
        (a, b) => Number(b.belongsToCurrentCase) - Number(a.belongsToCurrentCase)
      ),
    [blacklistableIdentifiers]
  );

  const columns: NullableTableColumns<BlacklistableCaseIdentifier> = {
    identifier: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("alerts.confirmFraud.identifiers.table.identifier.column.title"),
      render: (row) => `${row.identifier.identifierType.name ?? ""} ${row.element.name ?? ""}`,
    },
    value: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("alerts.confirmFraud.identifiers.table.value.column.title"),
      render: (row) => row.identifier.value ?? "",
    },
    blacklist: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("alerts.confirmFraud.identifiers.table.blacklist.column.title"),
      render: (row) => row.blacklist.name ?? "",
    },
    currentCase: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("alerts.confirmFraud.identifiers.table.currentCase.column.title"),
      render: (row) =>
        row.belongsToCurrentCase && <CheckmarkFilled className="text-supportSuccessInverse" />,
    },
    relatedCases: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("alerts.confirmFraud.identifiers.table.relatedCases.column.title"),
      render: (row) => (
        <button
          onClick={() => {
            setSelectedIdentifierId(row.id);
          }}
          title={`${row.linkedCases
            .slice(0, 3)
            .map((c) => c.properties.find((p) => p.property.code === "id")?.value.value)
            .join(", ")}, +${row.linkedCases.length - 3}`}
          className="cds--tag border-hidden hover:cursor-pointer cds--tag--sm cds--layout--size-sm cds--tag--blue"
        >
          <span className="cds--tag__label cds--tag--blue">+{row.linkedCases.length}</span>
        </button>
      ),
    },
  };

  return (
    <>
      <FixedTable
        pageSize={defaultPageSize}
        id="blacklist-identifiers-table"
        title={t("alerts.confirmFraud.identifiers.table.title")}
        description={t("alerts.confirmFraud.identifiers.table.description")}
        selectedRows={blacklistableIdentifiers.filter((item) => item.belongsToCurrentCase)}
        data={sortedIdentifiers}
        columns={columns}
        isSelectable={true}
        onSelect={handleSelect}
        isLoading={false}
        toolbar={() => (
          <TableToolbar aria-label="data table toolbar">
            <TableToolbarContent className="border-b border-solid border-borderSubtle01">
              <TableToolbarSearch
                id="searchBlacklistableIdentifiers"
                onSearchChange={setSearch}
                search={search}
              />
            </TableToolbarContent>
          </TableToolbar>
        )}
        filter={(row) => searchForIdentifier(search, row)}
        noRowsText={t("alerts.confirmFraud.identifiers.empty.title")}
      />
      <ModalTransition>
        {selectedIdentifierId && (
          <RelatedCasesModal
            onClose={() => {
              setSelectedIdentifierId(null);
            }}
            caseId={caseId}
            identifierId={selectedIdentifierId}
          />
        )}
      </ModalTransition>
    </>
  );
};

const searchForIdentifier = (search: string, identifier: BlacklistableCaseIdentifier) =>
  searchInString(search, identifier.identifier.identifierType.name) ||
  searchInString(search, identifier.identifier.value) ||
  searchInString(search, identifier.element.name) ||
  searchInString(search, identifier.blacklist.name);

const searchInString = (search: string, value: string) =>
  value.toLowerCase().includes(search.toLowerCase());
